import { useState, useEffect, useMemo } from "react";
import type { MentionProvider } from "@lumanity/ui/chat";
import { ChevronRight, FileText } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useProjectDetailContext } from "@/contexts/ProjectDetailContext";
import { useSectionAuditRecorder } from "@/hooks/useAuditRecorder";
import { useProjectTemplate } from "@/hooks/useProjects";
import { useAllActiveProjectTemplatesWithPrompts } from "@/hooks/useTemplates";
import { useAdminTemplateTypes, useAdminTemplateFormatTypes } from "@/hooks/useMasterData";
import { useReportGeneration } from "@/hooks/useReportGeneration";
import { usePromptLibraries } from "@/hooks/usePromptLibrary";
import { projectsApi, LUMANITY_DOC_STYLE } from "@/services/projectsApi";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ReportDocumentView } from "./outline/ReportDocumentView";
import { SectionFilterBar } from "./outline/SectionFilterBar";
import { PromptSelectDialog } from "./outline/PromptSelectDialog";
import type { Citation, GeneratedContent } from "./outline/GeneratedContentView";
import type { ExtractionColumnDetail } from "@/types/models";
import { buildGridReviewMap, filterAvailableExtractionPointIds } from "@/lib/extractionReview";
import type { Section } from "./outline/SortableRow";
import { buildReportMarkdownBasicCitations, buildSectionExtractionTableMarkdown, buildSectionNumberMap, buildTableOfContents, type ExtractionTableOptions } from "@/utils/exportMarkdown";
import { useAuth } from "@/contexts/AuthContext";
import { useUsers } from "@/hooks/useUsers";
import { parseReportCitations } from "@/utils/parseCitations";
import { artifactMarkdownQuery } from "@/lib/artifactMarkdown";
import { selectSectionArtifacts } from "@/lib/artifacts";
import { useArtifactMarkdown } from "@/hooks/useArtifactMarkdown";
import type { Artifact } from "@/services/projectsApi";
import type { ArtifactAppendix } from "./outline/GeneratedContentView";
import { withoutPendingSelections } from "@/lib/extractionSelection";

function buildColumnValue(data: string): string {
  try {
    const parsed = JSON.parse(data) as Record<string, unknown>;
    const parts: string[] = [];
    for (const key of ["population", "intervention", "comparator", "outcome"] as const) {
      if (parsed[key]) parts.push(`${key.charAt(0).toUpperCase() + key.slice(1)}: ${parsed[key]}`);
    }
    if (parsed.fields && typeof parsed.fields === "object") {
      for (const [k, v] of Object.entries(parsed.fields as Record<string, unknown>)) {
        const val = v && typeof v === "object" && "value" in v ? (v as { value: string }).value : String(v ?? "");
        if (val) parts.push(`${k}: ${val}`);
      }
    }
    return parts.join("; ");
  } catch {
    return data;
  }
}

function parseGeneratedText(raw: string): GeneratedContent {
  const { citations } = parseReportCitations(raw);
  return {
    text: raw,
    citations: citations.map((c): Citation => {
      const name = c.filename;
      const pageMatch = /\d+/.exec(c.page);
      const page = pageMatch ? `, p. ${pageMatch[0]}` : "";
      return { index: c.number, label: `${name}${page}`, filename: c.filename, page: c.page, sourceText: c.sourceText };
    }),
  };
}

const ReportWritingView = () => {
  const auth = useAuth();
  const { data: allUsers = [] } = useUsers();
  const { projectId, project, projectDocuments, activeStage, handleStageComplete, continueLabel, pageTitle, setChatDocumentIds, setStepStateNote, setMentionProvider } = useProjectDetailContext();
  const { data: activeTemplate, isLoading } = useProjectTemplate(projectId, 'Reporting');
  const { data: allExtractionTemplates = [] } = useAllActiveProjectTemplatesWithPrompts(projectId, 'Extraction');
  const qc = useQueryClient();

  const { data: templateTypes = [] } = useAdminTemplateTypes();
  const { data: formatTypes } = useAdminTemplateFormatTypes();
  const { data: allPromptLibraries = [] } = usePromptLibraries();

  // Build a lookup from source_prompt_library_id to original priming text
  const originalPrimingMap = useMemo(() => {
    const map: Record<string, string | null> = {};
    const pls = activeTemplate?.prompt_libraries ?? [];
    for (const pl of pls) {
      const sourcePl = allPromptLibraries.find((lib) => lib.id === pl.source_prompt_library_id);
      if (sourcePl) {
        map[pl.id] = sourcePl.priming ?? null;
      }
    }
    return map;
  }, [activeTemplate?.prompt_libraries, allPromptLibraries]);
  const reportingFormatTypeId = (formatTypes ?? []).find((ft) => ft.name === "Reporting")?.id ?? null;

  const extractionTemplateIds = useMemo(
    () => allExtractionTemplates.map((t) => t.template.id),
    [allExtractionTemplates]
  );

  const {
    data: extractionDocuments = [],
    isLoading: isExtractionLoading,
    isError: hasExtractionError,
  } = useQuery({
    queryKey: ["projects", projectId, "extraction-documents-all", extractionTemplateIds],
    queryFn: async () => {
      const results = await Promise.all(
        extractionTemplateIds.map((id) => projectsApi.getTemplateDocuments(projectId!, id, true))
      );
      // Pending rows are "Select from Evidence Base" picks with no extracted data yet.
      return withoutPendingSelections(results.flat());
    },
    enabled: !!projectId && extractionTemplateIds.length > 0,
  });

  // Each extraction template becomes a parent node; its prompt_libraries become children.
  // The modal uses parent_id=null items as selectable templates and children as read-only columns.
  const extractionPoints = useMemo(
    () =>
      allExtractionTemplates.flatMap(({ template, prompt_libraries }) => [
        { id: template.id, name: template.name, parent_id: null as string | null },
        ...prompt_libraries.map((pl) => ({ id: pl.id, name: pl.name, parent_id: template.id as string | null })),
      ]),
    [allExtractionTemplates]
  );

  // Derived once here so the source modal and the section flag read the same values.
  const gridReview = useMemo(
    () => buildGridReviewMap(extractionDocuments, extractionPoints),
    [extractionDocuments, extractionPoints]
  );

  const { data: artifacts } = useQuery({
    queryKey: ["projects", projectId, "artifacts"],
    queryFn: () => projectsApi.getArtifacts(projectId!),
    enabled: !!projectId,
  });

  const extractionTableOptions = useMemo<ExtractionTableOptions | undefined>(() => {
    if (extractionDocuments.length === 0) return undefined;
    return {
      records: extractionDocuments.map((td) => ({ documentId: td.documentId, data: td.data })),
      // Only leaf nodes (actual columns, not template header nodes) used as table columns
      columns: extractionPoints.filter((ep) => ep.parent_id != null).map((ep) => ({ id: ep.id, name: ep.name })),
      documents: projectDocuments.map((d) => ({
        id: d.id, filename: d.filename, title: d.title,
        author: d.author, year: d.year, sourceOrganisation: d.sourceOrganisation,
      })),
    };
  }, [extractionDocuments, extractionPoints, projectDocuments]);

  // Derive template type ID from the project's own type so custom templates (source_template_id = "custom")
  // resolve correctly instead of always producing null and bypassing prompt-library filtering.
  const templateTypeId = useMemo(
    () => templateTypes.find((t) => t.name.toUpperCase() === project?.projectType?.toUpperCase())?.id ?? null,
    [templateTypes, project]
  );

  // Scope the chat assistant to the union of every section's explicitly selected
  // source documents; fall back to all project documents when no section has any
  // configured yet (mirrors the per-section fallback used in handleGenerate).
  useEffect(() => {
    const projectDocumentIdSet = new Set(projectDocuments.map((d) => d.id));
    const unioned = new Set<string>();
    for (const pl of activeTemplate?.prompt_libraries ?? []) {
      for (const docId of pl.sources?.document_ids ?? []) {
        if (projectDocumentIdSet.has(docId)) unioned.add(docId);
      }
    }
    setChatDocumentIds(unioned.size > 0 ? Array.from(unioned) : null);
  }, [activeTemplate, projectDocuments, setChatDocumentIds]);

  const [generatedContent, setGeneratedContent] = useState<Record<string, GeneratedContent>>({});
  const [promptSelectSection, setPromptSelectSection] = useState<Section | null>(null);
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [hasMissingSources, setHasMissingSources] = useState(false);

  // Artefacts are shown live below each drafted section, from its current sources —
  // never saved into the section text, so they always reflect the artefact's
  // current version and can't be duplicated by a reload or an edit.
  const sectionArtifacts = useMemo(() => {
    const map: Record<string, Artifact[]> = {};
    for (const pl of activeTemplate?.prompt_libraries ?? []) {
      if (!generatedContent[pl.id]) continue;
      const selected = selectSectionArtifacts(pl.sources?.artifact_ids, artifacts);
      if (selected.length > 0) map[pl.id] = selected;
    }
    return map;
  }, [activeTemplate, generatedContent, artifacts]);

  const uniqueSectionArtifacts = useMemo(() => {
    const byUid = new Map<string, Artifact>();
    for (const list of Object.values(sectionArtifacts)) {
      for (const a of list) byUid.set(a.uid, a);
    }
    return Array.from(byUid.values());
  }, [sectionArtifacts]);

  const { markdownByUid, loadingUids } = useArtifactMarkdown(projectId, uniqueSectionArtifacts);

  const artifactAppendix: Record<string, ArtifactAppendix> = {};
  for (const [sectionId, list] of Object.entries(sectionArtifacts)) {
    artifactAppendix[sectionId] = {
      markdown: list.map((a) => markdownByUid[a.uid]).filter(Boolean).join("\n\n"),
      isLoading: list.some((a) => loadingUids.has(a.uid)),
      names: list.map((a) => a.name),
    };
  }

  // Plain-text summary of report drafting progress — fed into the chat assistant's
  // system prompt. In-flight generation status (Queued/Processing/Error) is
  // deliberately excluded — too volatile for a static state snapshot.
  const reportStateNote = useMemo(() => {
    const sections = activeTemplate?.prompt_libraries ?? [];
    if (sections.length === 0) return null;

    const draftedSections = sections.filter((pl) => generatedContent[pl.id]);
    const missingSourceNames = sections
      .filter((pl) => !generatedContent[pl.id]
        && (pl.sources?.document_ids?.length ?? 0) === 0
        && filterAvailableExtractionPointIds(
          pl.sources?.extraction_point_ids ?? [], extractionPoints, gridReview
        ).length === 0)
      .map((pl) => pl.name);
    const totalWords = draftedSections.reduce((sum, pl) => {
      const text = generatedContent[pl.id]?.text.trim() ?? "";
      return sum + (text ? text.split(/\s+/).length : 0);
    }, 0);

    let note = `Report Writing (${activeTemplate?.template.name ?? "active template"}): ${draftedSections.length} of ${sections.length} sections drafted` +
      (totalWords > 0 ? ` (~${totalWords} words total)` : "") + ".";
    if (missingSourceNames.length > 0) {
      const shown = missingSourceNames.slice(0, 10);
      note += ` Sections missing sources: ${shown.join(", ")}${missingSourceNames.length > 10 ? ", …" : ""}.`;
    }
    return note;
  }, [activeTemplate, generatedContent, extractionPoints, gridReview]);

  useEffect(() => {
    setStepStateNote(reportStateNote);
    return () => setStepStateNote(null);
  }, [reportStateNote, setStepStateNote]);

  // @-mention provider: resolved fresh at send time (see useAssistantThread in
  // @lumanity/ui), so content is always current — never frozen at mention time.
  const mentionProvider = useMemo<MentionProvider>(() => {
    const sections = activeTemplate?.prompt_libraries ?? [];

    return {
      search: async (query) => {
        const q = query.toLowerCase();
        return sections
          .filter((pl) => pl.name.toLowerCase().includes(q))
          .slice(0, 20)
          .map((pl) => ({ id: pl.id, label: pl.name, description: "Report section" }));
      },
      resolveContent: (item) => {
        const pl = sections.find((s) => s.id === item.id);
        const text = generatedContent[item.id]?.text.trim();
        // Artefacts by name only — their content is image data, not useful context.
        const artifactNames = (sectionArtifacts[item.id] ?? []).map((a) => a.name);
        const artifactLine = artifactNames.length > 0 ? `\n\nArtefacts: ${artifactNames.join(", ")}` : "";
        return `${pl?.name ?? item.label}${text ? `:\n${text}${artifactLine}` : " (not drafted yet)"}`;
      },
    };
  }, [activeTemplate, generatedContent, sectionArtifacts]);

  useEffect(() => {
    setMentionProvider(mentionProvider);
    return () => setMentionProvider(null);
  }, [mentionProvider, setMentionProvider]);

  // Grouped in render order: each root immediately followed by its children. The server
  // orders by ordinal and can interleave roots and children, so a flat ordinal sort breaks
  // parent-child grouping. Section numbering ("2.1") is derived from this order.
  const sections: Section[] = useMemo(() => {
    const pls = activeTemplate?.prompt_libraries ?? [];
    const grouped: Section[] = [];
    for (const root of pls.filter((pl) => !pl.parent_id)) {
      grouped.push(root);
      grouped.push(...pls.filter((pl) => pl.parent_id === root.id));
    }
    return grouped;
  }, [activeTemplate]);

  const recordSectionChange = useSectionAuditRecorder("Report Writing", sections);

  const handleExport = async () => {
    if (!activeTemplate) return;

    const pls = activeTemplate.prompt_libraries ?? [];
    const roots = pls.filter((pl) => !pl.parent_id);
    const orderedSections: typeof pls = [];
    for (const root of roots) {
      orderedSections.push(root);
      orderedSections.push(...pls.filter((pl) => pl.parent_id === root.id));
    }

    setIsExporting(true);
    try {
      const preparedFor = (project?.clientNames ?? []).join(", ");
      const memberNames = (project?.memberIds ?? [])
        .map((id) => allUsers.find((u) => u.id === id))
        .filter(Boolean)
        .map((u) => `${u!.first_name} ${u!.last_name}`.trim() || u!.email);
      const currentUserName = auth.user?.firstName && auth.user?.lastName
        ? `${auth.user.firstName} ${auth.user.lastName}`
        : (auth.user?.email ?? "");
      const preparedBy = memberNames.length > 0
        ? memberNames.join(", ")
        : currentUserName;
      const reportDate = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

      let imageBase64: string | undefined;
      try {
        const resp = await fetch("/lumanity_header.png");
        if (resp.ok) {
          const buf = await resp.arrayBuffer();
          const arr = new Uint8Array(buf);
          let binary = "";
          for (let i = 0; i < arr.length; i++) binary += String.fromCharCode(arr[i]);
          imageBase64 = btoa(binary);
        }
      } catch { /* skip image if unavailable */ }

      const coverPageMarkdown = [
        `# ${activeTemplate.template.name}`,
        "",
        `## Report date:`,
        "",
        reportDate,
        "",
        `## Prepared for:`,
        "",
        preparedFor,
        "",
        `## Prepared by:`,
        "",
        preparedBy,
      ].join("\n");

      const toc = buildTableOfContents(orderedSections);
      const contentMap = Object.fromEntries(
        Object.entries(generatedContent).map(([id, gc]) => [id, gc.text])
      );
      // Artefacts aren't in the section text; fetch their markdown (cached from rendering).
      const artifactsBySection: Record<string, string> = {};
      await Promise.all(
        Object.entries(sectionArtifacts).map(async ([sectionId, list]) => {
          const blocks = await Promise.all(list.map((a) => qc.fetchQuery(artifactMarkdownQuery(projectId, a))));
          const markdown = blocks.filter(Boolean).join("\n\n");
          if (markdown) artifactsBySection[sectionId] = markdown;
        })
      );
      const bodyMd = buildReportMarkdownBasicCitations(
        activeTemplate.template.name, orderedSections, contentMap, undefined, artifactsBySection
      );
      const filename = `${activeTemplate.template.name.replace(/\s+/g, "_")}_report.docx`;

      const customProperties: Record<string, string> = {};
      if (project?.id) customProperties.pico_project_id = project.id;
      if (project?.name) customProperties.pico_project_name = project.name;
      if (project?.code) customProperties.pico_project_code = project.code;
      if (project?.clientNames?.length) customProperties.pico_client_name = project.clientNames.join(", ");
      if (project?.clientIds?.length) customProperties.pico_client_id = project.clientIds.join(", ");
      if (project?.therapyArea) customProperties.pico_therapy_area = project.therapyArea;
      if (project?.indication) customProperties.pico_indication = project.indication;
      if (project?.condition) customProperties.pico_condition = project.condition;
      if (project?.objective) customProperties.pico_objective = project.objective;
      if (project?.projectType) customProperties.pico_project_type = project.projectType;

      await projectsApi.generateMarkdownWord(toc + bodyMd, filename, LUMANITY_DOC_STYLE, {
        is_coverpage_required: true,
        coverpage_content: coverPageMarkdown,
        ...(imageBase64 ? { cover_image_base64: imageBase64 } : {}),
      }, customProperties);
    } finally {
      setIsExporting(false);
    }
  };

  const { sectionStatuses, generateSection, initializeSections } = useReportGeneration();

  // Reset section filter whenever the active template changes (covers "workflow step entered")
  useEffect(() => {
    setSelectedSectionId(null);
  }, [activeTemplate?.template.id]);

  // On mount, restore any previously generated content and resume in-progress polling
  useEffect(() => {
    if (!activeTemplate) return;
    const ids = activeTemplate.prompt_libraries.map((pl) => pl.id);
    initializeSections(
      projectId,
      activeTemplate.template.id,
      ids,
      (sectionId, text) => {
        setGeneratedContent((prev) => ({ ...prev, [sectionId]: parseGeneratedText(text) }));
      },
      (sectionId, msg) => console.error("Section generation failed:", sectionId, msg)
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTemplate?.template.id]);

  const handlePromptSelect = async (priming: string) => {
    if (!promptSelectSection) return;
    const previousPriming = promptSelectSection.priming ?? null;
    await projectsApi.updateProjectPromptLibrary(projectId, promptSelectSection.id, { priming });
    recordSectionChange(promptSelectSection.id, "edit_prompt", { priming: previousPriming }, { priming });
    qc.invalidateQueries({ queryKey: ["projects", projectId, "template", "Reporting"] });
    setPromptSelectSection(null);
  };

  const handleSaveEdit = (sectionId: string, newText: string) => {
    const previousText = generatedContent[sectionId]?.text ?? null;
    setGeneratedContent((prev) => {
      const existing = prev[sectionId];
      if (!existing || !newText) return prev;
      return { ...prev, [sectionId]: { ...existing, text: newText } };
    });
    projectsApi
      .saveReportData(projectId, {
        template_id: activeTemplate.template.id,
        prompt_library_id: sectionId,
        data: newText,
      })
      .then(() => {
        // The editor's Save button is always enabled, so a save that changed nothing is
        // not a change worth a trail entry.
        if (previousText !== newText) {
          recordSectionChange(sectionId, "edit_section", { content: previousText }, { content: newText });
        }
      })
      .catch((err) => console.error("Failed to save report edit:", err));
  };

  const handleSaveName = (sectionId: string, newName: string) => {
    const previousName = sections.find((s) => s.id === sectionId)?.name ?? null;
    projectsApi
      .updateProjectPromptLibrary(projectId, sectionId, { name: newName })
      .then(() => {
        recordSectionChange(sectionId, "rename_section", { name: previousName }, { name: newName });
        qc.invalidateQueries({ queryKey: ["projects", projectId, "template", "Reporting"] });
      })
      .catch((err) => console.error("Failed to save section name:", err));
  };

  const handleSaveInstruction = (sectionId: string, value: string) => {
    // The section's own priming, not the library default in originalPrimingMap — on a
    // second edit the default is no longer what the user changed away from.
    const previousPriming = sections.find((s) => s.id === sectionId)?.priming ?? null;
    projectsApi
      .updateProjectPromptLibrary(projectId, sectionId, { priming: value })
      .then(() => {
        recordSectionChange(sectionId, "edit_prompt", { priming: previousPriming }, { priming: value });
        qc.invalidateQueries({ queryKey: ["projects", projectId, "template", "Reporting"] });
      })
      .catch((err) => console.error("Failed to save AI instruction:", err));
  };

  const buildColumnValue = (td: { data: string | null }): string => {
    if (!td.data) return "";
    try {
      const parsed = JSON.parse(td.data) as Record<string, unknown>;
      const parts: string[] = [];
      for (const [k, v] of Object.entries(parsed)) {
        if (k === "fields" && v && typeof v === "object") {
          for (const [fk, fv] of Object.entries(v as Record<string, unknown>)) {
            const val = fv && typeof fv === "object" && "value" in fv ? (fv as { value: string }).value : String(fv ?? "");
            if (val) parts.push(`${fk}: ${val}`);
          }
        } else if (v && typeof v === "string") {
          parts.push(`${k.charAt(0).toUpperCase() + k.slice(1)}: ${v}`);
        }
      }
      return parts.join("; ");
    } catch {
      return td.data ?? "";
    }
  };

  const handleGenerate = async (sectionId: string) => {
    if (!activeTemplate) return;
    const template =
      qc.getQueryData<NonNullable<typeof activeTemplate>>(
        ["projects", projectId, "template", "Reporting"]
      ) ?? activeTemplate;
    const pl = template.prompt_libraries.find((p) => p.id === sectionId);
    if (!pl) return;
    const isRegenerate = Boolean(generatedContent[sectionId]);

    const projectDocumentIdSet = new Set(projectDocuments.map((d) => d.id));

    // Documents explicitly selected as sources — RAG-searched by the backend
    const selectedDocumentIds = (pl.sources?.document_ids ?? []).filter((id) =>
      projectDocumentIdSet.has(id)
    );
    // Unreviewed grids are gated at selection; filter again here so a source saved
    // before its grid reverted to pending review never reaches the output.
    const selectedExtractionPointIds = filterAvailableExtractionPointIds(
      pl.sources?.extraction_point_ids ?? [],
      extractionPoints,
      gridReview
    );
    // Sources store the register uid, but artefacts are indexed under their row id
    // (uids repeat across projects built from the same CEM). Only Ready artefacts
    // have been indexed, so anything else would just search an empty filter.
    const selectedArtifactRowIds = selectSectionArtifacts(pl.sources?.artifact_ids, artifacts).map((a) => a.id);
    const hasPublications = selectedDocumentIds.length > 0;
    const hasExtractionPoints = selectedExtractionPointIds.length > 0;
    const hasArtifacts = selectedArtifactRowIds.length > 0;

    // Publications and artefacts both drive RAG (artefact rows share the search index,
    // keyed by documentid); fall back to all project docs only when no source is selected.
    const documentIds: string[] =
      hasPublications || hasArtifacts
        ? [...selectedDocumentIds, ...selectedArtifactRowIds]
        : hasExtractionPoints
          ? []
          : projectDocuments.map((d) => d.id);

    // Extraction data is independent of publication selection. When extraction points are
    // selected, all documents from those templates are included automatically.
    const selectedTemplateIds = new Set(
      extractionPoints
        .filter((ep) => selectedExtractionPointIds.includes(ep.id) && ep.parent_id != null)
        .map((ep) => ep.parent_id!)
    );
    const extractionColumnDetails: ExtractionColumnDetail[] = hasExtractionPoints
      ? extractionDocuments
          .filter((td) => td.data && selectedTemplateIds.has(td.projectTemplateId))
          .map((td) => ({
            document_id: td.documentId,
            column_name: pl.name,
            column_value: buildColumnValue(td),
          }))
      : selectedDocumentIds.map((docId) => {
          const docsForDocument = extractionDocuments.filter((td) => td.documentId === docId);
          const columnValue = docsForDocument
            .map((td) => buildColumnValue(td))
            .filter(Boolean)
            .join("\n");
          return { document_id: docId, column_name: pl.name, column_value: columnValue };
        });

    // Clear existing content so the Queued/Processing loading states render (same as first generation)
    setGeneratedContent((prev) => {
      const next = { ...prev };
      delete next[sectionId];
      return next;
    });

    const contextText = pl.description_ai_output
      ? pl.description_ai_output
      : [pl.description, pl.description_context].filter(Boolean).join('\n\n');

    // Group: each root immediately followed by its children — same approach as the render-order
    // grouping at line ~379. The server can assign ordinals that interleave roots and children,
    // so we cannot rely on raw array order for correct numbering.
    const sortedSections: typeof template.prompt_libraries = [];
    for (const root of template.prompt_libraries.filter(s => !s.parent_id)) {
      sortedSections.push(root);
      sortedSections.push(...template.prompt_libraries.filter(s => s.parent_id === root.id));
    }
    const nums = buildSectionNumberMap(sortedSections);
    const outlineText = [
      'Report outline (for context only — confine your response to the section you are writing):',
      ...sortedSections.map(s => {
        const label = s.parent_id ? `  ${nums[s.id]}. ${s.name}` : `${nums[s.id]}. ${s.name}`;
        return s.id === sectionId ? `${label}  ← writing this section` : label;
      }),
    ].join('\n');

    const effectivePriming = contextText
      ? `${pl.priming ?? ''}\n\nSection Context:\n${contextText}\n\n${outlineText}`.trim()
      : `${pl.priming ?? ''}\n\n${outlineText}`.trim();

    const model = project.model ?? "opus-46";
    recordSectionChange(
      sectionId,
      isRegenerate ? "regenerate_section" : "generate_section",
      null,
      {
        document_count: documentIds.length,
        extraction_point_count: selectedExtractionPointIds.length,
        artifact_count: selectedArtifactRowIds.length,
        model,
      }
    );

    await generateSection(
      {
        projectId,
        templateId: template.template.id,
        promptLibraryId: sectionId,
        priming: effectivePriming,
        documentIds,
        extractionColumnDetails,
        reportingColumnName: pl.name,
        model,
      },
      (text) => {
        let fullText = text;
        if (extractionTableOptions) {
          const { citations: textCitations } = parseReportCitations(text);
          const sectionForTable = {
            ...pl,
            sources: {
              document_ids: selectedDocumentIds,
              extraction_point_ids: selectedExtractionPointIds,
            },
          };
          const tableMarkdown = buildSectionExtractionTableMarkdown(sectionForTable, extractionTableOptions, textCitations.length + 1);
          if (tableMarkdown) fullText = `${text}\n\n${tableMarkdown}`;
        }
        setGeneratedContent((prev) => ({
          ...prev,
          [sectionId]: parseGeneratedText(fullText),
        }));
        if (fullText !== text) {
          projectsApi.saveReportData(projectId, {
            template_id: activeTemplate.template.id,
            prompt_library_id: sectionId,
            data: fullText,
          }).catch(console.error);
        }
      },
      (errMsg) => console.error("Report generation failed:", errMsg)
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        Loading…
      </div>
    );
  }

  if (!activeTemplate) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        No template configured for this project.
      </div>
    );
  }

  return (
    <Card className="p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground">{pageTitle}</h2>
          <p className="text-sm text-muted-foreground mt-0.5">{activeTemplate.template.name}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleExport} disabled={isExporting}>
            <FileText className="w-4 h-4 mr-1.5" />
            {isExporting ? "Exporting…" : "Export Report"}
          </Button>
          {/* <Button variant="brand" size="sm" onClick={() => handleStageComplete(activeStage)}>
            {continueLabel}
            <ChevronRight className="w-4 h-4" />
          </Button> */}
        </div>
      </div>

      {hasMissingSources && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm">
          <span className="w-3 h-3 rounded-sm bg-red-400 shrink-0" />
          <p className="text-red-800">
            <span className="font-semibold">Red highlighted sections</span> indicate no sources
            have been selected. Add sources to these sections before generating content.
          </p>
        </div>
      )}
      <SectionFilterBar
        sections={sections}
        selectedSectionId={selectedSectionId}
        onSelect={setSelectedSectionId}
      />
      <ReportDocumentView
        sections={sections}
        generatedContent={generatedContent}
        sectionStatuses={sectionStatuses}
        documents={projectDocuments}
        onGenerate={handleGenerate}
        onRegenerate={handleGenerate}
        onSaveEdit={handleSaveEdit}
        onSaveName={handleSaveName}
        filterSectionId={selectedSectionId}
        projectId={projectId}
        activeTemplate={activeTemplate}
        templateQueryKey={["projects", projectId, "template", "Reporting"]}
        extractionPoints={extractionPoints}
        extractionDocuments={extractionDocuments}
        gridReview={gridReview}
        isExtractionLoading={isExtractionLoading}
        hasExtractionError={hasExtractionError}
        artifacts={artifacts}
        artifactAppendix={artifactAppendix}
        originalPrimingMap={originalPrimingMap}
        onSelectPrompt={(section) => setPromptSelectSection(section)}
        onSaveInstruction={handleSaveInstruction}
        onMissingSourcesChange={setHasMissingSources}
      />
      <PromptSelectDialog
        open={promptSelectSection !== null}
        onOpenChange={(open) => { if (!open) setPromptSelectSection(null); }}
        onSelect={handlePromptSelect}
        templateTypeId={templateTypeId}
        reportingFormatTypeId={reportingFormatTypeId}
      />
    </Card>
  );
};

export default ReportWritingView;
